import { useEffect, useMemo, useState } from "react";

// Fully dynamic catalog — the entire product list is built from /api/products (Stripe).
// No hardcoded product data is consulted for matching.
//
// Grouping rule:
//   base_name       = everything BEFORE the last "(" in the Stripe name, trimmed
//   variant_suffix  = content INSIDE the last parenthesis pair
//   Products with the same base_name are grouped into ONE card with N variants.
//   Products with no parenthesis in their name become a single-variant card.
//
// Variant suffix regex accepts a length RANGE too (e.g. "60-65cm / 100g"):
//     ^([\d-]+)\s*cm\s*\/\s*(\d+)\s*g$

const API_BASE = "";

const PLACEHOLDER_IMAGE = "/placeholder-product.svg";
const GENERIC_DESCRIPTION =
  "Prémium minőségű, kutikula-irányított emberi haj — magánszemélyeknek. Festhető, melegre formázható, megfelelő ápolás mellett évekig hordható.";

// ── Name parsing ─────────────────────────────────────────────────────────────
function splitBaseAndSuffix(rawName) {
  const name = (rawName || "").trim();
  const lastOpen = name.lastIndexOf("(");
  const lastClose = name.lastIndexOf(")");
  if (lastOpen === -1 || lastClose === -1 || lastClose < lastOpen) {
    return { baseName: name, suffixText: null };
  }
  return {
    baseName: name.slice(0, lastOpen).trim(),
    suffixText: name.slice(lastOpen + 1, lastClose).trim(),
  };
}

const VARIANT_SUFFIX_RE = /^([\d-]+)\s*cm\s*\/\s*(\d+)\s*g$/i;

function parseVariantSuffix(suffixText) {
  if (!suffixText) return null;
  const m = suffixText.match(VARIANT_SUFFIX_RE);
  if (!m) return null;
  // Keep length as string (supports ranges like "60-65"); weight as string too for consistent labeling.
  return { length: `${m[1]} cm`, weight: `${m[2]} g` };
}

function inferOrigin(baseName) {
  const n = (baseName || "").toLowerCase();
  if (n.startsWith("kazahsztáni") || n.startsWith("kazahsztani")) return "kazah";
  if (n.startsWith("európai") || n.startsWith("europai")) return "europai";
  return "europai"; // safe default
}

function slugify(text) {
  return (text || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "termek";
}

// ── Sort key ─────────────────────────────────────────────────────────────────
// Európai first, then Kazahsztáni; within each group alphabetical by base_name.
function compareProducts(a, b) {
  if (a.origin !== b.origin) return a.origin === "europai" ? -1 : 1;
  return a.name.localeCompare(b.name, "hu", { sensitivity: "base" });
}

// ── Main mapping: flat Stripe list → grouped product cards ───────────────────
// Stripe stores HUF as 2-decimal minor unit (e.g. 8_400_000 = 84 000 Ft) — divide by 100 for display.
const stripeAmountToHuf = (amount) =>
  typeof amount === "number" ? Math.round(amount / 100) : null;

function groupStripeProducts(stripeProducts) {
  const groups = new Map(); // baseName → group object

  for (const sp of stripeProducts) {
    const { baseName, suffixText } = splitBaseAndSuffix(sp.name);
    if (!baseName) continue;

    const parsed = parseVariantSuffix(suffixText);
    // Variant shape — parsed suffix if present, otherwise a placeholder single variant.
    const variant = parsed
      ? {
          length: parsed.length,
          weight: parsed.weight,
          stripe_price_id: sp.stripe_price_id || null,
          stripe_product_id: sp.stripe_product_id || null,
          unit_amount: stripeAmountToHuf(sp.unit_amount),
        }
      : {
          length: "",
          weight: "",
          stripe_price_id: sp.stripe_price_id || null,
          stripe_product_id: sp.stripe_product_id || null,
          unit_amount: stripeAmountToHuf(sp.unit_amount),
        };

    let group = groups.get(baseName);
    if (!group) {
      const origin = inferOrigin(baseName);
      const slug = slugify(baseName);
      const image =
        Array.isArray(sp.images) && sp.images.length > 0 && sp.images[0]
          ? sp.images[0]
          : PLACEHOLDER_IMAGE;
      const description = (sp.description || "").trim() || GENERIC_DESCRIPTION;
      group = {
        id: `${sp.stripe_product_id}/${slug}`, // used as React key + URL param combo
        stripe_product_id: sp.stripe_product_id,
        slug,
        name: baseName,
        origin,
        image,
        description,
        variants: [],
      };
      groups.set(baseName, group);
    }
    group.variants.push(variant);
  }

  // Sort variants within each group: by parsed leading length int, then weight
  const parseFirstNum = (s) => {
    const m = String(s || "").match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  };
  for (const g of groups.values()) {
    g.variants.sort((a, b) => {
      const dl = parseFirstNum(a.length) - parseFirstNum(b.length);
      if (dl !== 0) return dl;
      return parseFirstNum(a.weight) - parseFirstNum(b.weight);
    });
  }

  // Sort groups
  return Array.from(groups.values()).sort(compareProducts);
}

// Utility exported for consumers that need to look a product up by URL param
export function findProductByStripeId(products, stripeProductId) {
  if (!products || !stripeProductId) return null;
  return products.find((p) => p.stripe_product_id === stripeProductId) || null;
}

export default function useStripeProducts() {
  const [stripeProducts, setStripeProducts] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/products`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) {
          setStripeProducts(data);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const products = useMemo(() => {
    if (!stripeProducts) return null;
    return groupStripeProducts(stripeProducts);
  }, [stripeProducts]);

  return { products, loading, error };
}
